import type { AnalyticsFilters, UnusedLinesFilters } from '@/types/mobile-line.dto';

/**
 * Single home of the React Query keys (enforced by lint: no array literal
 * in a `queryKey:` outside this file).
 *
 * Phase 2 — HIERARCHICAL: every domain lives under a single root segment,
 * subkeys carry a kind segment (`['lines', 'detail', id]`), so invalidating
 * a domain root reaches every query of that domain. Functions with an
 * optional trailing arg return the shorter prefix for broad invalidation.
 *
 * Consequence assumed by design: broad invalidations are wider than the
 * phase-1 shapes (e.g. `lines.root()` now also refetches details, summary
 * and consumption) — that widening is the point: mutations previously
 * missed those caches.
 */
export const queryKeys = {
  auth: {
    user: () => ['auth', 'user'] as const,
    validateResetToken: (token: string | null) =>
      ['auth', 'validate-reset-token', token] as const,
  },

  organizations: {
    current: (tenantSlug?: string) =>
      tenantSlug === undefined
        ? (['organizations', 'current'] as const)
        : (['organizations', 'current', tenantSlug] as const),
    detail: (organizationId?: string) =>
      organizationId === undefined
        ? (['organizations', 'detail'] as const)
        : (['organizations', 'detail', organizationId] as const),
    all: () => ['organizations', 'list'] as const,
    switcher: () => ['organizations', 'switcher'] as const,
    notOnboarded: () => ['organizations', 'not-onboarded'] as const,
    deletionImpact: (organizationId: string | undefined) =>
      ['organizations', 'deletion-impact', organizationId] as const,
    members: (organizationId?: string) =>
      organizationId === undefined
        ? (['organizations', 'members'] as const)
        : (['organizations', 'members', organizationId] as const),
    pendingInvitations: (organizationId?: string) =>
      organizationId === undefined
        ? (['organizations', 'pending-invitations'] as const)
        : (['organizations', 'pending-invitations', organizationId] as const),
    invitation: (invitationId: string | undefined) =>
      ['organizations', 'invitation', invitationId] as const,
    memberAllowedSites: (
      organizationId: string | undefined,
      userId: string | undefined,
    ) =>
      ['organizations', 'member-allowed-sites', organizationId, userId] as const,
    sites: (organizationId: string | undefined) =>
      ['organizations', 'sites', organizationId] as const,
    notSyncedSites: (organizationId: string | undefined) =>
      ['organizations', 'not-synced-sites', organizationId] as const,
  },

  lines: {
    root: () => ['lines'] as const,
    travelEsimsRoot: () => ['lines', 'travel-esims'] as const,
    travelEsims: (lineId: string | undefined) =>
      ['lines', 'travel-esims', lineId] as const,
    list: (
      siteId: string | undefined,
      organizationId: string | undefined,
      params: {
        page: number;
        limit: number;
        filters?: unknown;
        searchText?: string;
      },
    ) => ['lines', 'list', siteId, organizationId, params] as const,
    detail: (lineId?: string) =>
      lineId === undefined
        ? (['lines', 'detail'] as const)
        : (['lines', 'detail', lineId] as const),
    summary: (
      siteId: string | undefined,
      organizationId: string | undefined,
    ) => ['lines', 'summary', siteId, organizationId] as const,
    consumption: (lineId: string | undefined, month?: number, year?: number) =>
      ['lines', 'consumption', lineId, month, year] as const,
    usage: (lineId: string | undefined, filters: AnalyticsFilters) =>
      [
        'lines',
        'usage',
        lineId,
        filters.startMonth,
        filters.startYear,
        filters.startDay,
        filters.endMonth,
        filters.endYear,
        filters.endDay,
        filters.productType,
      ] as const,
    light: (
      organizationId: string | undefined,
      siteId: string | undefined,
      filters?: unknown,
    ) => ['lines', 'light', organizationId, siteId, filters] as const,
    unused: (
      organizationId: string | undefined,
      siteId: string | undefined,
      filters: UnusedLinesFilters | null,
    ) =>
      [
        'lines',
        'unused',
        organizationId,
        siteId,
        filters?.startMonth,
        filters?.startYear,
        filters?.endMonth,
        filters?.endYear,
      ] as const,
  },

  billing: {
    root: () => ['billing'] as const,
    lineDetail: (lineId?: string, month?: number, year?: number) =>
      ['billing', 'line-detail', lineId, month, year] as const,
    linePeriods: (lineId?: string) =>
      ['billing', 'line-periods', lineId] as const,
    linesMap: (
      siteId: string | undefined,
      organizationId: string | undefined,
    ) => ['billing', 'lines-map', siteId, organizationId] as const,
    run: (runId?: string | null) => ['billing', 'run', runId] as const,
    runProgress: (runId?: string | null) =>
      ['billing', 'run-progress', runId] as const,
    latestActive: () => ['billing', 'latest-active'] as const,
    centerPreview: (runId?: string, siteId?: string) =>
      ['billing', 'center-preview', runId, siteId] as const,
  },

  checkout: {
    detail: (organizationId?: string) =>
      organizationId === undefined
        ? (['checkout', 'detail'] as const)
        : (['checkout', 'detail', organizationId] as const),
    count: (organizationId?: string) =>
      organizationId === undefined
        ? (['checkout', 'count'] as const)
        : (['checkout', 'count', organizationId] as const),
  },

  orders: {
    root: () => ['orders'] as const,
    list: (organizationId?: string) =>
      organizationId === undefined
        ? (['orders', 'list'] as const)
        : (['orders', 'list', organizationId] as const),
    detailRoot: () => ['orders', 'detail'] as const,
    detail: (orderId: string | undefined) =>
      ['orders', 'detail', orderId] as const,
    admin: () => ['orders', 'admin'] as const,
    pendingCount: () => ['orders', 'pending-count'] as const,
    travelEsimQrCode: (
      orderId: string | undefined,
      itemId: string | undefined,
    ) => ['orders', 'travel-esim-qr-code', orderId, itemId] as const,
    travelEsimsRoot: () => ['orders', 'travel-esims'] as const,
    travelEsimDetailRoot: () => ['orders', 'travel-esims', 'detail'] as const,
    travelEsim: (itemId: string | undefined) =>
      ['orders', 'travel-esims', 'detail', itemId] as const,
    travelEsimQrCodeById: (id: string | undefined) =>
      ['orders', 'travel-esims', 'qr-code', id] as const,
    travelEsims: (organizationId: string | undefined, params: unknown) =>
      ['orders', 'travel-esims', organizationId, params] as const,
  },

  catalog: {
    deviceModels: (activeOnly?: boolean) =>
      activeOnly === undefined
        ? (['catalog', 'device-models'] as const)
        : (['catalog', 'device-models', activeOnly] as const),
    deviceModel: (id: string | undefined) =>
      ['catalog', 'device-model', id] as const,
    deviceVariants: (deviceModelId?: string, activeOnly?: boolean) =>
      deviceModelId === undefined
        ? (['catalog', 'device-variants'] as const)
        : (['catalog', 'device-variants', deviceModelId, activeOnly] as const),
    accessories: (activeOnly?: boolean) =>
      activeOnly === undefined
        ? (['catalog', 'accessories'] as const)
        : (['catalog', 'accessories', activeOnly] as const),
    operators: () => ['catalog', 'operators'] as const,
    travelEsimRoot: () => ['catalog', 'travel-esim'] as const,
    travelEsimGrid: () => ['catalog', 'travel-esim', 'grid'] as const,
    travelEsimLookupRoot: () => ['catalog', 'travel-esim', 'lookup'] as const,
    travelEsimLookup: (
      organizationId: string | undefined,
      lineId: string | undefined,
      email: string | undefined,
    ) =>
      ['catalog', 'travel-esim', 'lookup', organizationId, lineId, email] as const,
    travelEsimDestinations: (organizationId: string | undefined) =>
      ['catalog', 'travel-esim', 'destinations', organizationId] as const,
    travelEsimQuote: (
      organizationId: string | undefined,
      country: string | undefined,
      departureDate: string | undefined,
      returnDate: string | undefined,
    ) =>
      [
        'catalog',
        'travel-esim',
        'quote',
        organizationId,
        country,
        departureDate,
        returnDate,
      ] as const,
    phonePlans: (
      organizationId: string | undefined,
      operatorId: string | undefined,
      mobileLineType?: string,
    ) =>
      [
        'catalog',
        'phone-plans',
        organizationId,
        operatorId,
        mobileLineType,
      ] as const,
    phonePlansByOrganization: (organizationId: string | undefined) =>
      ['catalog', 'phone-plans-by-organization', organizationId] as const,
    plansAvailableToChange: (
      planId: string | undefined,
      organizationId: string | undefined,
    ) =>
      [
        'catalog',
        'phone-plans-available-to-change',
        planId,
        organizationId,
      ] as const,
  },

  simCards: {
    detail: (id: string | undefined) => ['sim-cards', 'detail', id] as const,
    byOperator: (operator: string | undefined) =>
      ['sim-cards', 'by-operator', operator] as const,
    stock: (organizationId: string | undefined, operator: string | undefined) =>
      ['sim-cards', 'stock', organizationId, operator] as const,
  },

  identities: {
    root: () => ['identities'] as const,
    byOrganization: (organizationId: string | undefined) =>
      ['identities', 'organization', organizationId] as const,
    bySite: (
      siteId: string | undefined,
      organizationId: string | undefined,
    ) => ['identities', 'site', siteId, organizationId] as const,
    detail: (id?: string) =>
      id === undefined
        ? (['identities', 'detail'] as const)
        : (['identities', 'detail', id] as const),
  },

  invoices: {
    list: (organizationId?: string) =>
      organizationId === undefined
        ? (['invoices', 'list'] as const)
        : (['invoices', 'list', organizationId] as const),
  },

  notifications: {
    settings: (organizationId: string | undefined) =>
      ['notifications', 'settings', organizationId] as const,
  },

  addresses: {
    list: (organizationId?: string) =>
      organizationId === undefined
        ? (['addresses', 'list'] as const)
        : (['addresses', 'list', organizationId] as const),
  },

  adminSync: {
    root: () => ['admin-sync'] as const,
    organizations: () => ['admin-sync', 'organizations'] as const,
    overview: () => ['admin-sync', 'overview'] as const,
    history: (limit: number, kind: string) =>
      ['admin-sync', 'history', limit, kind] as const,
  },

  dashboard: {
    summary: (owner: { type?: string; id?: string } | null) =>
      ['dashboard', 'summary', owner?.type, owner?.id] as const,
    simStock: (owner: { type?: string; id?: string } | null) =>
      ['dashboard', 'sim-stock', owner?.type, owner?.id] as const,
    billing: (owner: { type?: string; id?: string } | null) =>
      ['dashboard', 'billing', owner?.type, owner?.id] as const,
  },

  alerts: {
    list: (
      organizationId: string | undefined,
      siteId: string | undefined,
      page: number,
      limit: number,
      searchText: string | undefined,
      from: string | undefined,
      to: string | undefined,
    ) =>
      [
        'alerts',
        'list',
        organizationId,
        siteId,
        page,
        limit,
        searchText,
        from,
        to,
      ] as const,
    summary: (
      organizationId: string | undefined,
      siteId: string | undefined,
      from: string | undefined,
      to: string | undefined,
    ) => ['alerts', 'summary', organizationId, siteId, from, to] as const,
  },
};
